import { useEffect, useState } from "react";
import Skeleton from "react-loading-skeleton";
import { useNavigate } from "react-router-dom";
import { getTimeAgo } from "../../helperFunctions";
import { useFetchInvitationsQuery } from "../../redux/features/cooperative/cooperative.api";
import { useFetchGuarantorLoanRequestsQuery } from "../../redux/features/loans/loans.api";
import { useAppSelector } from "../../redux/hooks";
import {
  SKELETON_BASE_COLOR,
  SKELETON_HIGHLIGHT_COLOR,
} from "../../utils/constants";
import { Button } from "../buttons/button";
import { Icon } from "../icons/icon";
import { EmptyState } from "../states/emptyState";

const NotificationsHeader = ({
  count,
  className = "",
  showCloseButton,
  close,
}: {
  count: number;
  className?: string;
  showCloseButton: boolean;
  close: () => void;
}) => {
  return (
    <div className={`text-sm flex items-center gap-4 font-medium ${className}`}>
      {showCloseButton ? (
        <button
          type="button"
          className=" w-fit"
          onClick={() => {
            close();
          }}
        >
          <Icon
            svg="caret-down"
            width={24}
            height={24}
            className="rotate-90 hover:!cursor-pointer hover:!scale-90"
          />
        </button>
      ) : (
        <Icon svg={"sms"} width={20} height={20} />
      )}

      <p className="text-2xl font-bold">Inbox</p>
    </div>
  );
};

export const Notifications = ({
  close = () => {},
  showCloseButton = false,
}: {
  close: () => void;
  showCloseButton?: boolean;
}) => {
  const [notificationCount, setNotificationCount] = useState<number>(0);
  const [notifications, setNotifications] = useState<
    {
      text: string;
      date: string;
      buttonText: string;
      to: string;
    }[]
  >([]);

  const navigate = useNavigate();

  const profile = useAppSelector((state) => {
    return state.auth.profile;
  });

  const { data: guarantorRequests, isFetching: fetchingRequests } =
    useFetchGuarantorLoanRequestsQuery(
      { detail: true },
      {
        refetchOnMountOrArgChange: true,
        skip: profile?.type !== "member",
      }
    );

  const { data: invitations, isFetching: fetchingInvitations } =
    useFetchInvitationsQuery(undefined, {
      refetchOnMountOrArgChange: true,
      skip: profile?.type !== "member",
    });

  useEffect(() => {
    setNotificationCount(
      (guarantorRequests?.data?.length ?? 0) + (invitations?.data?.length ?? 0)
    );

    setNotifications([
      ...(guarantorRequests?.data?.map((i) => ({
        text: `${i?.MembershipLoanRequestID?.MemberID?.User?.FirstName} ${i?.MembershipLoanRequestID?.MemberID?.User?.LastName} has requested you to stand as guarantor for a loan`,
        date: i.DateAdded,
        buttonText: "View Request",
        to: "/app/invitations/loan/guarantee",
      })) ?? []),
      ...(invitations?.data?.map((i) => ({
        text: `You were invited to join ${i?.Cooperative?.CoopName}`,
        date: i.DateUpdated,
        buttonText: "View Invite",
        to: "/app/invitations",
      })) ?? []),
    ]);
  }, [guarantorRequests, invitations]);

  return (
    <div className="h-full flex flex-col">
      <div className="py-3 md:py-4 px-6 border-b sticky top-0 z-10 bg-white flex items-center justify-between">
        <NotificationsHeader
          count={notificationCount}
          className={""}
          showCloseButton={showCloseButton}
          close={close}
        />
      </div>

      {[0, "", undefined, null].includes(notificationCount) &&
      !fetchingRequests ? (
        <div className="py-6 mt-auto flex-1 flex items-center justify-center">
          <EmptyState
            image={
              <img
                src={`/assets/images/empty_state_invite.svg`}
                alt="empty_state"
                className="md:max-w-[150px]"
              />
            }
            title="You have no new messages"
            className="my-auto"
          />
        </div>
      ) : undefined}

      <div className="flex flex-col flex-1 bg-white">
        {![0, "", undefined, null].includes(notificationCount) &&
          notifications.map((request, idx) => {
            return (
              <div
                key={`notif-${idx}`}
                className="px-6 py-4 flex flex-col gap-[10px] border-b last:border-b bg-light-green-bg"
              >
                <div className="flex gap-4 justify-between items-center text-sm">
                  <p className="opacity-80 font-medium">{request.text}</p>
                  <p className="min-w-max opacity-50">
                    {`${getTimeAgo(request.date)}`}
                  </p>
                </div>

                <Button
                  text={request.buttonText}
                  onClick={() => {
                    navigate(request.to);
                    close();
                  }}
                  type="button"
                  className="!text-xs !px-4 !py-2 !max-w-max"
                />
              </div>
            );
          })}
      </div>

      {fetchingRequests || fetchingInvitations ? (
        <div className="flex flex-col justify-center items-center">
          {Array.from({ length: 5 }).map((_, i) => {
            return (
              <div
                key={`notification-${i}`}
                className="flex flex-col gap-2.5 py-4 border-b w-full px-6"
              >
                <Skeleton
                  width={"100%"}
                  height={"32px"}
                  baseColor={SKELETON_BASE_COLOR}
                  highlightColor={SKELETON_HIGHLIGHT_COLOR}
                  className="rounded-lg"
                />

                <Skeleton
                  width={"67px"}
                  height={"32px"}
                  baseColor={SKELETON_BASE_COLOR}
                  highlightColor={SKELETON_HIGHLIGHT_COLOR}
                  className="rounded-lg"
                />
              </div>
            );
          })}
        </div>
      ) : undefined}
    </div>
  );
};
